import { ChevronDown, ChevronUp } from '@audio-network/componentlibrary/icons'
import { FilterButtonProps, WrapperComponentProps } from './FilterButton.types'
import React, { memo, useEffect, useRef, useState } from 'react'

import { Tooltip } from '@audio-network/componentlibrary'
import clsx from 'clsx'
import styles from './FilterButton.module.css'

// Will wrap the button in a tooltip if the button is too narrow to display the title
const WrapperComponent = memo(
	({ children, expanded, requiresTooltip, title }: WrapperComponentProps) => {
		if (requiresTooltip) {
			return (
				<Tooltip
					id={`filter-tooltip-${title}`}
					className={expanded && styles.hidden}
					content={title}
				>
					{children}
				</Tooltip>
			)
		}

		return <>{children}</>
	}
)

WrapperComponent.displayName = 'FilterButtonWrapper'

const FilterButton = ({
	'aria-expanded': expanded,
	className,
	Icon,
	onClick,
	mobile,
	primary,
	secondary,
	role,
	title,
	...props
}: FilterButtonProps): JSX.Element => {
	const [requiresTooltip, setRequiresTooltip] = useState(false)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const topLevelButton = !!((primary || secondary) && !mobile)

	useEffect(() => {
		if (!topLevelButton || !wrapperRef.current) {
			return
		}

		const THRESHOLD = 56
		const filtersWrapper = document.querySelector(
			'[data-search-filters]'
		) as HTMLDivElement

		const updateTooltipRequirement = (): void => {
			if (!wrapperRef.current) {
				return
			}

			// Check the width of the button to determine if it needs a tooltip
			const width = wrapperRef.current.getBoundingClientRect().width
			// Check if there is an open filter to determine if the button should be wrapped
			const hasActiveFilter = filtersWrapper?.dataset.searchOpenFilter
			setRequiresTooltip(width <= THRESHOLD && !hasActiveFilter)
		}

		// Watch for changes in the button's width (it will need a tooltip if text is not displayed)
		const resizeObserver = new ResizeObserver(updateTooltipRequirement)
		resizeObserver.observe(wrapperRef.current)

		// Watch for open filters (we don't want to show tooltips if there is an open filter)
		const mutationObserver = new MutationObserver(updateTooltipRequirement)
		if (filtersWrapper) {
			mutationObserver.observe(filtersWrapper, { attributes: true })
		}

		// Initial check
		updateTooltipRequirement()

		return () => {
			resizeObserver.disconnect()
			mutationObserver.disconnect()
		}
	}, [topLevelButton])

	return (
		<div ref={wrapperRef}>
			<WrapperComponent
				title={title}
				requiresTooltip={requiresTooltip}
				expanded={expanded === true}
			>
				<button
					aria-expanded={expanded}
					className={clsx(
						styles.filterButton,
						primary && styles.primary,
						className
					)}
					onClick={onClick}
					role={role}
					{...props}
				>
					{Icon && Icon}
					<span className={styles.title}>{title}</span>
					{expanded ? (
						<ChevronUp className={styles.chevron} />
					) : (
						<ChevronDown className={styles.chevron} />
					)}
				</button>
			</WrapperComponent>
		</div>
	)
}

export default FilterButton
